// Placing an availability-panel visit: every entry at one start, all or
// nothing (owner decision, 13 Sept).
//
//  1. Re-check the day with a fresh one-day band read. A start no longer in a
//     band is "taken": nothing is held.
//  2. Hold each entry in p_selection (display) order with holdUnit — never
//     holdServiceUnit, which stamps the cart party before it knows whether the
//     hold succeeded.
//  3. On the first refusal release every hold this Add placed
//     (cancel_cart_booking with the cart token) and read each verdict. A
//     release that cannot be confirmed stays a live hold checkout would
//     charge, so its line is returned to be added and named — never hidden.
//  4. On success the caller adds every line, stamps the visit's party ONCE and
//     opens the identity step / cart.

import { describeReason } from "@vertu/shared";
import { staleMenuRefusal } from "./special-date";
import type { WidgetService, WidgetUnit } from "../types";
import type { CartItem } from "./cart";
import { getCartToken, type BookingWindow, type HoldFailure } from "./booking";
import { supabase } from "../config";
import { buildUnitCartLine, holdUnit } from "./unit-cart";
import {
  containsStart,
  holdVisitEntries,
  type DayBands,
  type HoldFailureLike,
} from "./availability-logic";

export type VisitEntry = {
  unit: WidgetUnit;
  guests: number;
  /** The hold's length (the option's fixed length, or the chosen/first tier, or the service's). */
  minutes: number;
  /** A variable-length option: the chosen length is part of what was bought. */
  variableLength: boolean;
  basis: "per_person" | "per_item";
};

type Line = Omit<CartItem, "id">;

export type PlaceVisitOutcome =
  | { kind: "placed"; lines: Line[] }
  | { kind: "taken" }
  | {
      kind: "refused";
      entry: VisitEntry;
      failure: HoldFailureLike;
      released: string[];
      /** Lines whose hold could not be confirmed released — add them and say so. */
      kept: { entry: VisitEntry; line: Line }[];
    };

/**
 * Cancel one of this cart's pending holds and READ the verdict (the shared
 * cancelCartBooking discards it). ok, or "not pending / not found" (already
 * gone), is a confirmed release; anything else — a token mismatch, an error,
 * a network failure — is unknown.
 */
export async function releaseHold(bookingId: string): Promise<"released" | "unknown"> {
  try {
    const { data, error } = await supabase.rpc("cancel_cart_booking", {
      p_booking_id: bookingId,
      p_cart_token: getCartToken(),
    });
    if (error) return "unknown";
    const d = data as { ok?: unknown; reason?: unknown } | null;
    return d && (d.ok === true || d.reason === "not_pending_or_not_found") ? "released" : "unknown";
  } catch {
    return "unknown";
  }
}

export async function placeVisit(input: {
  service: WidgetService;
  /** Display (= p_selection) order. */
  entries: VisitEntry[];
  /** Guests already in this visit at this start (its lines already in the cart). */
  extraVisitGuests?: number;
  startMs: number;
  /** The venue's zone: the panel showed and held the start in it, so the cart shows it in it too. */
  tz?: string;
  /** Fresh one-day band read for the start's day. */
  recheck: () => Promise<DayBands | "error">;
  /** Unrounded display price (rand) — only used when the hold returns no unit_price_cents. */
  fallbackPrice: (unit: WidgetUnit) => number | null;
  lengthLabel: (minutes: number) => string;
  brandName: string | null;
  onPhase?: (phase: "checking" | "holding") => void;
}): Promise<PlaceVisitOutcome> {
  const { service, entries, startMs } = input;
  input.onPhase?.("checking");
  const fresh = await input.recheck();
  // A failed re-check fails OPEN: the hold has the final say.
  if (fresh !== "error" && !containsStart(fresh.bands, startMs)) return { kind: "taken" };

  input.onPhase?.("holding");
  const startsAt = new Date(startMs).toISOString();
  const visitGuests = entries.reduce((n, e) => n + e.guests, 0) + Math.max(0, input.extraVisitGuests ?? 0);
  const outcome = await holdVisitEntries<VisitEntry, Line>(entries, {
    hold: async (entry) => {
      const window: BookingWindow = {
        starts_at: startsAt,
        ends_at: new Date(startMs + entry.minutes * 60_000).toISOString(),
        party_size: entry.guests,
      };
      const hold = await holdUnit({ serviceId: service.id, unitId: entry.unit.id, window });
      if (!hold.ok) return { ok: false, failure: hold as HoldFailure };
      const perPerson = entry.basis === "per_person";
      const line = buildUnitCartLine({
        service,
        unit: entry.unit,
        hold,
        window,
        quantity: perPerson ? entry.guests : 1,
        unitPrice: input.fallbackPrice(entry.unit),
        visitGuests,
        pricingBasis: perPerson ? "per_person" : "per_unit",
        brandName: input.brandName,
      });
      // The length the hold actually took (a fixed option length wins on the
      // server) — the cart shows it, since a grouped visit hides each line's window.
      const s = Date.parse(String(line.metadata.starts_at ?? ""));
      const e = Date.parse(String(line.metadata.ends_at ?? ""));
      const heldMinutes = Number.isFinite(s) && Number.isFinite(e) && e > s ? Math.round((e - s) / 60_000) : entry.minutes;
      return {
        ok: true,
        bookingId: hold.booking_id,
        line: {
          ...line,
          description: entry.variableLength
            ? `${line.description} · ${input.lengthLabel(heldMinutes)}`
            : line.description,
          metadata: {
            ...line.metadata,
            option_led: true,
            session_minutes: heldMinutes,
            ...(input.tz ? { tenant_tz: input.tz } : {}),
          },
        },
      };
    },
    release: releaseHold,
  });
  if (outcome.kind === "placed") return { kind: "placed", lines: outcome.placed.map((p) => p.line) };
  return {
    kind: "refused",
    entry: outcome.entry,
    failure: outcome.failure,
    released: outcome.released,
    kept: outcome.kept.map((k) => ({ entry: k.entry, line: k.line })),
  };
}

const TAKEN = new Set([
  "resource_unavailable",
  "unit_sold_out",
  "service_capacity_exceeded",
  "plan_capacity_exceeded",
  "capacity_overflow",
  "no_capacity",
]);

/** The notice for a refused Add: names the refused option, never a slug or a count. */
export function refusalMessage(i: {
  entry: VisitEntry;
  failure: HoldFailureLike;
  hm: string;
  noun: { one: string; other: string };
  kept: VisitEntry[];
  participantForms: boolean;
}): string {
  const who = `${i.entry.unit.name} for ${i.entry.guests} ${i.entry.guests === 1 ? i.noun.one : i.noun.other}`;
  const head = TAKEN.has(i.failure.reason)
    ? `${who} at ${i.hm} was just taken`
    : `${who} at ${i.hm} couldn't be held — ${(
        // The availability panel shows no menu chooser.
        staleMenuRefusal(i.failure.reason, false) ?? describeReason(i.failure)
      ).replace(/\.$/, "")}`;
  if (i.kept.length === 0) return `${head}, so nothing was added.`;
  const names = i.kept.map((k) => k.unit.name).join(" and ");
  return `${head}. We couldn't confirm that ${names} was released, so it is in your visit — nothing is charged without you seeing it at checkout; remove it there if you don't want it.${i.participantForms ? " It may ask for guest details there too." : ""}`;
}
